"use client";

import { Button } from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { Icon } from "@repo/dashboard-core";
import { useTranslations } from "next-intl";

import { deleteFile } from "@/lib/actions/files";
import { displayName } from "@/lib/utils/file-helpers";

interface DeleteButtonProps {
  fileName: string;
  onDeleted: () => void | Promise<void>;
}

export function DeleteButton({ fileName, onDeleted }: DeleteButtonProps) {
  const t = useTranslations("my-files");

  function openConfirm() {
    modals.openConfirmModal({
      title: t("delete-modal.title"),
      size: "sm",
      centered: true,
      overlayProps: { blur: 5 },
      labels: {
        confirm: t("delete-modal.yes"),
        cancel: t("delete-modal.no"),
      },
      confirmProps: { color: "red" },
      children: (
        <p className="break-all">
          {t("delete-modal.description", { fileName: displayName(fileName) })}
        </p>
      ),
      onConfirm: async () => {
        const { success, message } = await deleteFile(fileName);
        if (!success) {
          notifications.show({
            title: t("error"),
            message,
            color: "red",
          });
          return;
        }
        notifications.show({
          title: t("success.title"),
          message: t("success.description"),
          color: "green",
        });
        await onDeleted();
      },
    });
  }

  return (
    <Button
      variant="filled"
      color="red"
      size="xs"
      leftSection={<Icon name="trash" size={14} />}
      onClick={openConfirm}
    >
      {t("table.delete")}
    </Button>
  );
}
